import { onLocaleChange, t } from "../i18n";
import { setupPresetCrud } from "./controls/preset-crud";
import { setupSourceAdjust } from "./controls/source-adjust";
import { setupMethodSection } from "./controls/method";
import { setupFamilyPanels } from "./controls/family-panels";
import { setupOutputSection } from "./controls/output";
import { createInfoTooltip } from "./info-tooltip";
import { createInitialState, effectiveDialValues } from "../state";
import type {
  DemoState,
  RemapPreset,
  Store,
} from "../state";
import { PRESET_CONTROLLED_KEYS } from "./tone-presets";
import { applyMethodPartial, type MethodPartial } from "../util/method-patch";
import type { CarryOverStats, PreprocessStats } from "../util/labelmap-import";
import { remapLabelmap, countPreservedEntries } from "../util/labelmap-remap";
import { fillSmallIslands } from "../util/region-ops";
import { pushUndoSnapshot } from "../util/region-undo";
import { makeCustomSelect } from "./custom-select";
import { wrapRangeAsSlider, type SliderVariant } from "./custom-slider";
import { compositeTransparentPixelsOverWhite } from "./image-data";

// The redesigned controls panel is split into four numbered sections per
// the brutalist + light-vaporwave spec (`amitone-design/studio.html` §2):
//
//   01 METHOD                - family + algorithm picker
//   02 BLACK / WHITE THRESH. - blackThreshold + whiteThreshold (yellow IX)
//   03 ALGORITHM + STAMP     - Family A params or Family B Custom Pattern
//                              (violet IX, swaps based on `state.family`)
//   04 OUTPUT                - Export PNG (violet) + Copy PNG buttons
//
// Sections 01/02/04 are static; section 03 swaps between A-panel and
// B-panel depending on the current family. Visibility within section 03
// is gated per algorithm/stamp by `syncVisibility` (preserved from the
// previous demo).

export interface ControlsHandles {
  /** Export-scale picker (1×/2×/4×) - lives in section 05 next to the
   * Export PNG button so the user can dial in scale right before
   * clicking export. main.ts reads `value` for current scale. */
  exportScaleSelect: HTMLSelectElement;
  /** Primary export button - Export PNG (violet variant). */
  exportButton: HTMLButtonElement;
  /** Secondary copy button - Copy PNG to clipboard. */
  copyButton: HTMLButtonElement;
}

function createFamilyStarterPatch(family: "A" | "B" | "C"): MethodPartial {
  const defaults = createInitialState();
  return {
    family,
    algorithmA: defaults.algorithmA,
    cellSize: defaults.cellSize,
    stampType: defaults.stampType,
    stampCoverage: defaults.stampCoverage,
    polarity: defaults.polarity,
    density: defaults.density,
    blackThreshold: defaults.blackThreshold,
    whiteThreshold: defaults.whiteThreshold,
    gridAngleDeg: defaults.gridAngleDeg,
    shapeAngleDeg: defaults.shapeAngleDeg,
    shapeFlipX: defaults.shapeFlipX,
    shapeFlipY: defaults.shapeFlipY,
    algorithmJitter: defaults.algorithmJitter,
    grainStrength: defaults.grainStrength,
    grainScale: defaults.grainScale,
    matrixSize: defaults.matrixSize,
    serpentine: defaults.serpentine,
    fsStrength: defaults.fsStrength,
    atkinsonStrength: defaults.atkinsonStrength,
    brightness: defaults.brightness,
    contrast: defaults.contrast,
    isGrayscale: defaults.isGrayscale,
    blurRadius: defaults.blurRadius,
    patternBrightness: defaults.patternBrightness,
    patternContrast: defaults.patternContrast,
    patternBlendMode: defaults.patternBlendMode,
    patternScale: defaults.patternScale,
    patternRotationDeg: defaults.patternRotationDeg,
    patternFlipX: defaults.patternFlipX,
    patternFlipY: defaults.patternFlipY,
    patternNoise: defaults.patternNoise,
    patternNoiseScale: defaults.patternNoiseScale,
    patternHalftoneEnabled: defaults.patternHalftoneEnabled,
    patternHalftoneCellSize: defaults.patternHalftoneCellSize,
    patternHalftoneStampType: defaults.patternHalftoneStampType,
    patternHalftoneStampCoverage: defaults.patternHalftoneStampCoverage,
    patternHalftonePolarity: defaults.patternHalftonePolarity,
    patternHalftoneDensity: defaults.patternHalftoneDensity,
    patternHalftoneBlackThreshold: defaults.patternHalftoneBlackThreshold,
    patternHalftoneWhiteThreshold: defaults.patternHalftoneWhiteThreshold,
    patternHalftoneGridAngleDeg: defaults.patternHalftoneGridAngleDeg,
    patternHalftoneShapeAngleDeg: defaults.patternHalftoneShapeAngleDeg,
    patternHalftoneShapeFlipX: defaults.patternHalftoneShapeFlipX,
    patternHalftoneShapeFlipY: defaults.patternHalftoneShapeFlipY,
    patternHalftoneAlgorithmJitter: defaults.patternHalftoneAlgorithmJitter,
    patternHalftoneNoiseScale: defaults.patternHalftoneNoiseScale,
    patternInvert: defaults.patternInvert,
    patternOpacity: defaults.patternOpacity,
  };
}

interface SectionGroup {
  section: HTMLElement;
  body: HTMLDivElement;
  head: HTMLHeadingElement;
}

function makeSection(
  ix: string,
  titleKey: string,
  variant: "" | "violet" | "yellow" = "",
): SectionGroup {
  const section = document.createElement("section");
  section.className = "section";

  const head = document.createElement("h3");
  head.className = `section-head${variant ? " " + variant : ""}`;

  const titleSpan = document.createElement("span");
  titleSpan.dataset["i18n"] = titleKey;
  // Set initial text immediately so sections outside the .controls aside
  // (e.g. the AREA MASK section in the Region panel) render their title
  // without needing createControls' syncI18n pass.
  titleSpan.textContent = t(titleKey);
  onLocaleChange(() => {
    titleSpan.textContent = t(titleKey);
  });
  head.appendChild(titleSpan);

  const ixSpan = document.createElement("span");
  // I2: badge colour mirrors the section accent so METHOD/ALGO STAMP get
  // yellow badges and SOURCE ADJUST gets a violet badge.
  ixSpan.className = variant ? `ix ix-${variant}` : "ix";
  ixSpan.textContent = ix;
  head.appendChild(ixSpan);

  section.appendChild(head);

  const body = document.createElement("div");
  body.className = "section-body";
  section.appendChild(body);

  return { section, body, head };
}

function makeRow(): HTMLDivElement {
  const row = document.createElement("div");
  row.className = "row";
  return row;
}

function closeOpenTooltips(scope: ParentNode = document): void {
  void scope;
}

// `.row-label` cluster: a real <label for=htmlFor> + an optional info button
// as a SIBLING of the label (NOT inside, otherwise label-click forwarding
// would also toggle the tooltip).
function makeLabelText(
  labelKey: string,
  tooltipKey: string | undefined,
  htmlFor: string,
): HTMLSpanElement {
  const wrap = document.createElement("span");
  wrap.className = "row-label";

  const label = document.createElement("label");
  label.htmlFor = htmlFor;
  label.dataset["i18n"] = labelKey;
  wrap.appendChild(label);

  if (tooltipKey) {
    const tip = createInfoTooltip(t(tooltipKey));
    onLocaleChange(() => tip.setText(t(tooltipKey)));
    wrap.appendChild(tip.element);
  }

  return wrap;
}

function rangeDecimalPlaces(input: HTMLInputElement): number {
  const step = input.step;
  if (!step.includes(".")) return 0;
  return step.length - step.indexOf(".") - 1;
}

function stepRange(input: HTMLInputElement, direction: -1 | 1): void {
  const min = Number(input.min);
  const max = Number(input.max);
  const step = Number(input.step);
  const value = Number(input.value);
  const next = Math.min(max, Math.max(min, value + step * direction));
  input.value = next.toFixed(rangeDecimalPlaces(input));
  input.dispatchEvent(new Event("input", { bubbles: true }));
}

function makeStepperButtons(input: HTMLInputElement): HTMLSpanElement {
  const buttons = document.createElement("span");
  buttons.className = "stepper-buttons";
  buttons.addEventListener("click", () => {
    closeOpenTooltips(buttons.closest(".controls") ?? document);
  });

  const up = document.createElement("button");
  up.type = "button";
  up.className = "step-button step-up";
  up.setAttribute("aria-label", "Increase value");
  up.addEventListener("click", (event) => {
    event.preventDefault();
    event.stopPropagation();
    closeOpenTooltips();
    stepRange(input, 1);
  });

  const down = document.createElement("button");
  down.type = "button";
  down.className = "step-button step-down";
  down.setAttribute("aria-label", "Decrease value");
  down.addEventListener("click", (event) => {
    event.preventDefault();
    event.stopPropagation();
    closeOpenTooltips();
    stepRange(input, -1);
  });

  buttons.appendChild(up);
  buttons.appendChild(down);
  return buttons;
}

function makeRange(
  min: number,
  max: number,
  step: number,
  value: number,
): HTMLInputElement {
  const input = document.createElement("input");
  input.type = "range";
  input.min = String(min);
  input.max = String(max);
  input.step = String(step);
  input.value = String(value);
  return input;
}

function makeSelect<T extends string>(
  options: ReadonlyArray<readonly [T, string]>,
  value: T,
): HTMLSelectElement {
  const select = document.createElement("select");
  for (const [v, labelKey] of options) {
    const opt = document.createElement("option");
    opt.value = v;
    opt.dataset["i18n"] = labelKey;
    select.appendChild(opt);
  }
  select.value = value;
  return select;
}

// Build a two-row slider: label first, then a horizontal value cluster.
interface SliderRowOptions {
  labelKey: string;
  tipKey: string;
  ctrlId: string;
  min: number;
  max: number;
  step: number;
  value: number;
  variant?: SliderVariant;
  formatValue?: (n: number) => string;
}

interface SliderRow {
  row: HTMLDivElement;
  input: HTMLInputElement;
  valueEl: HTMLSpanElement;
}

function buildSliderRow(opts: SliderRowOptions): SliderRow {
  const row = makeRow();
  const input = makeRange(opts.min, opts.max, opts.step, opts.value);
  input.id = opts.ctrlId;
  const labelText = makeLabelText(opts.labelKey, opts.tipKey, input.id);
  const valueEl = document.createElement("span");
  valueEl.className = "row-value";
  const fmt = opts.formatValue ?? ((n: number) => String(n));
  valueEl.textContent = fmt(opts.value);
  const cluster = document.createElement("span");
  cluster.className = "row-value-cluster";
  const stepper = makeStepperButtons(input);
  const slider = wrapRangeAsSlider(input, opts.variant ?? "violet");
  row.appendChild(labelText);
  cluster.appendChild(slider);
  cluster.appendChild(valueEl);
  cluster.appendChild(stepper);
  row.appendChild(cluster);
  return { row, input, valueEl };
}

interface SelectRowOptions<T extends string> {
  labelKey: string;
  tipKey?: string;
  ctrlId: string;
  options: ReadonlyArray<readonly [T, string]>;
  value: T;
}

interface SelectRow {
  row: HTMLDivElement;
  select: HTMLSelectElement;
  root: HTMLDivElement;
  refresh: () => void;
}

function buildSelectRow<T extends string>(
  opts: SelectRowOptions<T>,
): SelectRow {
  const row = makeRow();
  const select = makeSelect(opts.options, opts.value);
  select.id = opts.ctrlId;
  const label = makeLabelText(opts.labelKey, opts.tipKey, select.id);
  row.appendChild(label);
  row.appendChild(select);
  const handle = makeCustomSelect(select);
  row.appendChild(handle.root);
  return { row, select, root: handle.root, refresh: handle.refresh };
}

// Build a `.row.row-file` with a brutalist trigger button instead of the
// OS-default "Choose File" widget. The native `<input type="file">` is
// hidden via `.sr-only` and triggered by clicking the styled `<label>`,
// which also forwards keyboard focus / Enter for accessibility. A small
// filename pill below the trigger reflects the most recent selection.
interface FileRowOptions {
  triggerKey: string;
  tipKey?: string;
  ctrlId: string;
  accept: string;
}

interface FileRow {
  row: HTMLDivElement;
  input: HTMLInputElement;
  filenameEl: HTMLSpanElement;
}

function buildFileRow(opts: FileRowOptions): FileRow {
  const row = makeRow();
  row.classList.add("row-file");

  const input = document.createElement("input");
  input.type = "file";
  input.accept = opts.accept;
  input.id = opts.ctrlId;
  input.className = "sr-only";

  // Trigger cluster: the `.file-trigger` <label for=> opens the picker on
  // click. The info button is a SIBLING of the label (not inside) so the
  // controls invariant "no info button inside label[for]" still holds -
  // otherwise label-click forwarding would also fire when the user clicks
  // the info button.
  const triggerCluster = document.createElement("span");
  triggerCluster.className = "file-trigger-cluster";

  const trigger = document.createElement("label");
  trigger.className = "btn file-trigger";
  trigger.setAttribute("for", input.id);
  trigger.tabIndex = 0;

  const triggerLabel = document.createElement("span");
  triggerLabel.dataset["i18n"] = opts.triggerKey;
  trigger.appendChild(triggerLabel);
  trigger.appendChild(input);

  // Allow Enter / Space to trigger picker (default <label> forwarding
  // already handles click; keyboard activation needs an explicit hook).
  trigger.addEventListener("keydown", (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      input.click();
    }
  });

  // Drag-drop onto the trigger button itself. Mirrors the canvas drop
  // pattern in main.ts: dragover.preventDefault() to let the browser
  // accept the drop, then on drop assign the first file to the input
  // via DataTransfer and dispatch a synthetic change event so the
  // existing change listener (and consumer logic) fires unchanged.
  // The dragover marker drives the brutalist hover state via
  // `[data-dragover='true']` in styles.css.
  trigger.addEventListener("dragover", (event) => {
    event.preventDefault();
    if (event.dataTransfer) event.dataTransfer.dropEffect = "copy";
    trigger.dataset["dragover"] = "true";
  });
  trigger.addEventListener("dragleave", () => {
    delete trigger.dataset["dragover"];
  });
  trigger.addEventListener("drop", (event) => {
    event.preventDefault();
    delete trigger.dataset["dragover"];
    const dropped = event.dataTransfer?.files?.[0];
    if (!dropped) return;
    const dt = new DataTransfer();
    dt.items.add(dropped);
    input.files = dt.files;
    input.dispatchEvent(new Event("change", { bubbles: true }));
  });

  triggerCluster.appendChild(trigger);

  if (opts.tipKey) {
    const tip = createInfoTooltip(t(opts.tipKey));
    onLocaleChange(() => tip.setText(t(opts.tipKey!)));
    triggerCluster.appendChild(tip.element);
  }

  const filenameEl = document.createElement("span");
  filenameEl.className = "file-filename";
  filenameEl.dataset["i18n"] = "action.upload.empty";

  input.addEventListener("change", () => {
    const file = input.files?.[0];
    filenameEl.textContent = file ? file.name : t("action.upload.empty");
    if (file) filenameEl.dataset["i18n"] = "";
  });

  row.appendChild(triggerCluster);
  row.appendChild(filenameEl);
  return { row, input, filenameEl };
}

function readImageDataFromFile(
  file: File,
  transparentPixels: "preserve" | "white",
): Promise<ImageData> {
  return createImageBitmap(file).then((bitmap) => {
    const canvas = document.createElement("canvas");
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("2d context unavailable");
    ctx.drawImage(bitmap, 0, 0);
    const image = ctx.getImageData(0, 0, bitmap.width, bitmap.height);
    return transparentPixels === "white"
      ? compositeTransparentPixelsOverWhite(image)
      : image;
  });
}

// ---------------------------------------------------------------------------
// B1 - AREA MASK section (formerly section 01 inside createControls).
//
// Extracted into its own exported function so main.ts can render it into
// the Region tab panel instead of the Tone tab controls aside.
// ---------------------------------------------------------------------------

export interface AreaMaskHandles {
  /** Update the carry-over / preprocess status line (mirrors old
   *  ControlsHandles.setLabelmapStatus). Pass null to clear. */
  setLabelmapStatus(
    stats: CarryOverStats | null,
    preprocessStats?: PreprocessStats | null,
  ): void;
}

interface AreaMaskOptions {
  onOpenMaskPicker?: () => void;
  onMaskFile?: (file: File) => void;
  onDeleteMask?: () => void;
}

function firstImageFile(files: FileList | File[]): File | null {
  for (const item of Array.from(files)) {
    if (item.type.startsWith("image/")) return item;
  }
  return null;
}

function clampMinIslandPixels(value: number): number {
  if (!Number.isFinite(value)) return 1;
  return Math.min(1000, Math.max(1, Math.round(value)));
}

export function createAreaMaskSection(
  parent: HTMLElement,
  store: Store,
  options: AreaMaskOptions = {},
): AreaMaskHandles {
  // ── RR-1: Section 01 AREA MASK ────────────────────────────────────────────
  // Contains only the mask loader (Replace/Load mask button + help text).
  // All remap + cleanup controls live in Section 02 REMAP below.
  const labelmapSection = makeSection("01", "label.labelmap", "violet");
  parent.appendChild(labelmapSection.section);

  // Info tooltip lives inline only once a mask is loaded. Before that, guidance
  // is shown as always-visible help text below the button, with no tooltip
  // trigger in the DOM.
  const maskBtnTip = createInfoTooltip(t("region.replaceMaskHelp"));
  maskBtnTip.element.classList.add("region-mask-btn-tip");

  // Load/replace mask button. main.ts passes the picker and file handler so the
  // Region tab owns the control while retaining the top-bar file input.
  const maskBtn = document.createElement("button");
  maskBtn.type = "button";
  maskBtn.className = "region-mask-btn btn";

  const maskBtnFace = document.createElement("span");
  maskBtnFace.className = "region-mask-btn-face";

  const maskBtnIcon = document.createElement("span");
  maskBtnIcon.setAttribute("aria-hidden", "true");
  maskBtnIcon.textContent = "▦";

  const maskBtnLabel = document.createElement("span");
  maskBtnFace.appendChild(maskBtnIcon);
  maskBtnFace.appendChild(maskBtnLabel);
  maskBtn.appendChild(maskBtnFace);

  // Delete mask button. Only visible when a mask is already loaded.
  // Sits beside the Replace mask button in the same flex row.
  const deleteMaskBtn = document.createElement("button");
  deleteMaskBtn.type = "button";
  deleteMaskBtn.className = "region-delete-mask-btn btn";
  deleteMaskBtn.setAttribute("aria-label", t("region.deleteMaskButton"));

  const deleteMaskBtnLabel = document.createElement("span");
  deleteMaskBtnLabel.dataset["i18n"] = "region.deleteMaskButton";
  deleteMaskBtnLabel.textContent = t("region.deleteMaskButton");
  deleteMaskBtn.appendChild(deleteMaskBtnLabel);

  deleteMaskBtn.addEventListener("click", () => {
    const confirmMsg = t("region.deleteMaskConfirm");
    if (!window.confirm(confirmMsg)) return;
    options.onDeleteMask?.();
  });

  // Flex row: button stretches, info-btn pinned to the right.
  // Delete mask + two-row mask-action layout - Yuko request 2026-05-15
  const maskBtnRow = document.createElement("div");
  maskBtnRow.className = "region-mask-btn-row";
  maskBtnRow.appendChild(maskBtn);
  maskBtnRow.appendChild(deleteMaskBtn);
  labelmapSection.body.appendChild(maskBtnRow);

  const maskBtnHelp = document.createElement("p");
  maskBtnHelp.className = "region-mask-help";
  labelmapSection.body.appendChild(maskBtnHelp);

  function syncMaskButtonText(): void {
    const hasMask = store.get().labelMap !== null;
    const btnKey = hasMask
      ? "region.replaceMaskButton"
      : "region.loadMaskButton";
    maskBtnLabel.dataset["i18n"] = btnKey;
    maskBtnLabel.textContent = t(btnKey);
    maskBtnHelp.hidden = hasMask;
    maskBtnIcon.hidden = hasMask;
    // Delete mask button: only meaningful once a mask is loaded.
    deleteMaskBtn.hidden = !hasMask;
    deleteMaskBtnLabel.textContent = t("region.deleteMaskButton");
    deleteMaskBtn.setAttribute("aria-label", t("region.deleteMaskButton"));
    if (hasMask) {
      delete maskBtnHelp.dataset["i18n"];
      maskBtnHelp.textContent = "";
      if (!maskBtnTip.element.parentElement) {
        maskBtnRow.appendChild(maskBtnTip.element);
      }
      maskBtnTip.setText(t("region.replaceMaskHelp"));
    } else {
      maskBtnHelp.dataset["i18n"] = "region.loadMaskHelp";
      maskBtnHelp.textContent = t("region.loadMaskHelp");
      maskBtnTip.element.remove();
    }
  }

  maskBtn.addEventListener("click", () => {
    options.onOpenMaskPicker?.();
  });
  maskBtn.addEventListener("dragover", (event) => {
    event.preventDefault();
    if (event.dataTransfer) event.dataTransfer.dropEffect = "copy";
    maskBtn.dataset["dragover"] = "true";
  });
  maskBtn.addEventListener("dragleave", () => {
    delete maskBtn.dataset["dragover"];
  });
  maskBtn.addEventListener("drop", (event) => {
    event.preventDefault();
    delete maskBtn.dataset["dragover"];
    const files = event.dataTransfer?.files;
    if (!files) return;
    const file = firstImageFile(files);
    if (!file) return;
    options.onMaskFile?.(file);
  });

  function canUseBroadMaskDrop(): boolean {
    return store.get().labelMap === null;
  }

  parent.addEventListener("dragover", (event) => {
    if (event.defaultPrevented || !canUseBroadMaskDrop()) return;
    // Browsers protect file metadata during dragover (only filename / size
    // is exposed on drop), so dataTransfer.files is empty / null here on
    // real platforms. Detect file drags via dataTransfer.types instead -
    // "Files" is set when a file is being dragged from outside the page.
    const types = event.dataTransfer?.types;
    const hasFile = types
      ? Array.prototype.indexOf.call(types, "Files") !== -1
      : false;
    if (!hasFile) return;
    event.preventDefault();
    if (event.dataTransfer) event.dataTransfer.dropEffect = "copy";
    parent.dataset["maskDragover"] = "true";
  });

  parent.addEventListener("dragleave", (event) => {
    if (event.currentTarget === event.target) {
      delete parent.dataset["maskDragover"];
    }
  });

  parent.addEventListener("drop", (event) => {
    if (event.defaultPrevented || !canUseBroadMaskDrop()) return;
    const files = event.dataTransfer?.files;
    if (!files) return;
    // The dragover handler advertised this region as a valid drop target
    // (any "Files" drag), so we MUST preventDefault here regardless of
    // whether the dropped file is an image. Otherwise dropping a
    // non-image (e.g. a PDF) would let the browser navigate away from
    // the app to the dropped resource.
    event.preventDefault();
    delete parent.dataset["maskDragover"];
    const file = firstImageFile(files);
    if (!file) return;
    options.onMaskFile?.(file);
  });

  syncMaskButtonText();
  onLocaleChange(syncMaskButtonText);

  // Toggle primary-attention styling: yellow Load-image look when no mask is
  // loaded; revert to a plain sidebar button once a mask exists. Driven via
  // [data-has-mask], synced to state.labelMap.
  function syncMaskLoadedState(): void {
    if (store.get().labelMap !== null) {
      maskBtn.dataset["hasMask"] = "true";
    } else {
      delete maskBtn.dataset["hasMask"];
    }
  }
  syncMaskLoadedState();
  // Initialise delete button visibility (syncMaskButtonText sets it too, but
  // syncMaskLoadedState runs before it - initialise here to be explicit).
  deleteMaskBtn.hidden = store.get().labelMap === null;

  // ── RR-2: Section 02 REMAP ────────────────────────────────────────────────
  // Flat section: no internal subheads. Order:
  //   area-status-card (prominent detected-areas count)
  //   Max areas slider
  //   Use raw + Advanced checkboxes
  //   Remap button
  //   Drop islands + Re-segment buttons
  //   Min island pixels slider
  // The labelmap status details are kept off-canvas and shown as a click
  // tooltip from the area-status-card.
  const remapSection = makeSection("02", "section.remap", "yellow");
  remapSection.body.classList.add("remap-section-body");
  parent.appendChild(remapSection.section);
  // R10 gating: REMAP section only makes sense when a labelmap mask is loaded.
  // Hide entirely until a mask exists; subscribe below toggles on state change.
  remapSection.section.hidden = store.get().labelMap === null;

  // ── Prominent areas-detected card (RR-2) ─────────────────────────────────
  // Sits at the very top of the REMAP section so the count is immediately
  // visible as the user adjusts parameters. The card also doubles as the
  // click trigger for compact labelmap details.
  // R11 a11y: card is interactive (click + keyboard toggles a tooltip), so
  // expose it as a button with aria-expanded. The polite live region for
  // count announcements lives in a separate visually-hidden sibling so it
  // is not nested inside a button (where SR support is unreliable) and so
  // it can carry the full localized phrase ("N areas detected") instead of
  // just the bare integer.
  const areaStatusCard = document.createElement("div");
  areaStatusCard.className = "area-status-card";
  areaStatusCard.setAttribute("role", "button");
  areaStatusCard.setAttribute("aria-expanded", "false");
  areaStatusCard.tabIndex = 0;

  const areaStatusCount = document.createElement("span");
  areaStatusCount.className = "area-status-count";
  // Hidden from SR (true text is in the sr-only live region below) so the
  // bare integer inside the button is not double-read.
  areaStatusCount.setAttribute("aria-hidden", "true");

  const areaStatusLabel = document.createElement("span");
  areaStatusLabel.className = "area-status-label";
  areaStatusLabel.dataset["i18n"] = "label.areasDetected";
  areaStatusLabel.textContent = t("label.areasDetected");
  areaStatusLabel.setAttribute("aria-hidden", "true");

  areaStatusCard.appendChild(areaStatusCount);
  areaStatusCard.appendChild(areaStatusLabel);
  remapSection.body.appendChild(areaStatusCard);

  // Visually hidden polite live region carrying the full announceable
  // sentence; sits as a sibling of the button (not a descendant) so SR
  // engines reliably announce updates.
  const areaStatusLive = document.createElement("span");
  areaStatusLive.className = "sr-only area-status-live";
  areaStatusLive.setAttribute("role", "status");
  areaStatusLive.setAttribute("aria-live", "polite");
  remapSection.body.appendChild(areaStatusLive);

  let labelmapStatusText = "";
  let hasLabelmapStatus = false;
  let areaStatusTipEl: HTMLElement | null = null;
  let syncStatusDisclosure: () => void = () => {};

  // R11 lifecycle: when tooltip is open, listen for outside-clicks, Escape,
  // scroll, and resize so the tooltip can never be left dangling if the user
  // navigates away or the layout shifts. Detached again on hide.
  function onTooltipOutsidePointer(event: MouseEvent): void {
    if (!areaStatusTipEl) return;
    const target = event.target as Node | null;
    if (!target) return;
    if (areaStatusCard.contains(target)) return;
    if (areaStatusTipEl.contains(target)) return;
    hideAreaStatusTooltip();
  }
  function onTooltipKeydown(event: KeyboardEvent): void {
    if (event.key === "Escape" && areaStatusTipEl) {
      event.preventDefault();
      hideAreaStatusTooltip();
      areaStatusCard.focus();
    }
  }
  function onTooltipReflow(): void {
    if (!areaStatusTipEl) return;
    // Cheapest correct option for a tooltip that sits at fixed pixel
    // coordinates: hide on viewport changes rather than re-positioning live.
    hideAreaStatusTooltip();
  }
  function attachTooltipListeners(): void {
    document.addEventListener("mousedown", onTooltipOutsidePointer, true);
    document.addEventListener("keydown", onTooltipKeydown, true);
    window.addEventListener("scroll", onTooltipReflow, true);
    window.addEventListener("resize", onTooltipReflow);
  }
  function detachTooltipListeners(): void {
    document.removeEventListener("mousedown", onTooltipOutsidePointer, true);
    document.removeEventListener("keydown", onTooltipKeydown, true);
    window.removeEventListener("scroll", onTooltipReflow, true);
    window.removeEventListener("resize", onTooltipReflow);
  }

  function hideAreaStatusTooltip(): void {
    if (areaStatusTipEl?.parentNode) {
      areaStatusTipEl.parentNode.removeChild(areaStatusTipEl);
    }
    if (areaStatusTipEl !== null) detachTooltipListeners();
    areaStatusTipEl = null;
    syncStatusDisclosure();
  }

  function positionAreaStatusTooltip(tip: HTMLElement): void {
    const rect = areaStatusCard.getBoundingClientRect();
    const margin = 8;
    const offset = 6;
    const tipRect = tip.getBoundingClientRect();
    const tipWidth = tipRect.width || 340;
    const tipHeight = tipRect.height || 80;
    const maxLeft = window.innerWidth - tipWidth - margin;
    const left = Math.max(margin, Math.min(rect.left, maxLeft));
    const top =
      window.innerHeight - rect.bottom < tipHeight + offset + margin
        ? rect.top - tipHeight - offset
        : rect.bottom + offset;

    tip.style.left = `${left}px`;
    tip.style.top = `${Math.max(margin, top)}px`;
  }

  function showAreaStatusTooltip(): void {
    if (!hasLabelmapStatus) return;
    const text = labelmapStatusText.trim();
    if (!text) return;

    hideAreaStatusTooltip();
    const tip = document.createElement("span");
    tip.className = "info-tip-portal info-tip-portal-wide area-status-tip";
    tip.textContent = text;
    document.body.appendChild(tip);
    areaStatusTipEl = tip;
    positionAreaStatusTooltip(tip);
    attachTooltipListeners();
    syncStatusDisclosure();
  }

  function renderLabelmapStatus(
    stats: CarryOverStats | null,
    preprocessStats?: PreprocessStats | null,
  ): void {
    const parts: string[] = [];

    if (preprocessStats != null) {
      parts.push(
        t("status.labelmap.preprocess.reduced", {
          raw: preprocessStats.raw,
          k: preprocessStats.resulting,
        }),
      );
      if (preprocessStats.capHit) {
        parts.push(t("status.labelmap.preprocess.capHit"));
      }
    }

    if (
      stats !== null &&
      (stats.carriedOver > 0 || stats.dropped > 0 || stats.added > 0)
    ) {
      parts.push(
        t("status.labelmap.carryOver", {
          carried: stats.carriedOver,
          dropped: stats.dropped,
          added: stats.added,
        }),
      );
    }

    if (stats !== null && stats.splits > 0) {
      parts.push(t("status.labelmap.splits", { splits: stats.splits }));
    }

    if (parts.length === 0) {
      labelmapStatusText = "";
      hasLabelmapStatus = false;
      hideAreaStatusTooltip();
      syncStatusDisclosure();
      return;
    }
    labelmapStatusText = parts.join(" ");
    hasLabelmapStatus = true;
    if (areaStatusTipEl) showAreaStatusTooltip();
    syncStatusDisclosure();
  }

  function syncRemapRegionInfo(): void {
    const n = store.get().regionEntries.length;
    // Update the prominent count in the area-status-card (visual only,
    // hidden from SR; the live region below carries the announceable text).
    areaStatusCount.textContent = String(n);
    // Show the card whenever a mask is loaded (n >= 0 with a mask); hide
    // entirely only when no mask has been loaded yet.
    areaStatusCard.hidden = store.get().labelMap === null;
    if (areaStatusCard.hidden) hideAreaStatusTooltip();
    // Sync the polite live region AND the button accessible name with the
    // full localized phrase. The button needs an explicit aria-label
    // because both its visible text children are aria-hidden (so the live
    // sibling can carry the announceable phrase without double-read).
    const phrase = areaStatusCard.hidden
      ? ""
      : `${n} ${t("label.areasDetected")}`;
    areaStatusLive.textContent = phrase;
    if (phrase) {
      areaStatusCard.setAttribute("aria-label", phrase);
    } else {
      areaStatusCard.removeAttribute("aria-label");
    }
    // Sync button affordance to availability: when no detailed labelmap
    // status exists yet (no Remap run, or mask cleared), keyboard / SR
    // users should not land on a button whose activation is a no-op.
    syncStatusDisclosure();
  }

  // Clicking the compact card shows detailed labelmap status as a portal
  // tooltip instead of expanding inline text into the panel.
  areaStatusCard.style.cursor = "pointer";
  areaStatusCard.addEventListener("click", () => {
    if (!hasLabelmapStatus) return;
    if (areaStatusTipEl) {
      hideAreaStatusTooltip();
    } else {
      showAreaStatusTooltip();
    }
  });
  areaStatusCard.addEventListener("keydown", (event) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    areaStatusCard.click();
  });

  syncStatusDisclosure = () => {
    const expanded = areaStatusTipEl !== null;
    areaStatusCard.dataset["expanded"] = String(expanded);
    areaStatusCard.setAttribute("aria-expanded", String(expanded));
    // When there is no detailed labelmap status to expand, the button is a
    // no-op: aria-disable it and remove from tab order so keyboard users
    // skip it. When details exist, restore tab focus and expanded state.
    if (hasLabelmapStatus) {
      areaStatusCard.removeAttribute("aria-disabled");
      areaStatusCard.tabIndex = 0;
      areaStatusCard.style.cursor = "pointer";
    } else {
      areaStatusCard.setAttribute("aria-disabled", "true");
      areaStatusCard.tabIndex = -1;
      areaStatusCard.style.cursor = "default";
    }
  };

  syncRemapRegionInfo();

  // ── Remap controls ────────────────────────────────────────────────────────

  const PRESET_CONFIGS: Record<
    Exclude<RemapPreset, "custom">,
    { maxK: number; deltaE: number }
  > = {
    clean: { maxK: 16, deltaE: 8 },
    balanced: { maxK: 32, deltaE: 4 },
    preserve: { maxK: 64, deltaE: 0 },
  };

  const remapMaxKRow = buildSliderRow({
    labelKey: "param.remap.maxK",
    tipKey: "tip.remap.maxK",
    ctrlId: "ctrl-remapMaxK",
    min: 2,
    max: 64,
    step: 1,
    value: store.get().remapMaxK,
  });
  remapSection.body.appendChild(remapMaxKRow.row);

  // B1: Remap preset dropdown removed from UI per R5 spec.
  // The underlying state.remapPreset field is preserved for backward compat;
  // the select element is kept off-DOM so slider <-> preset sync logic continues to work.
  const remapPresetRow = buildSelectRow({
    labelKey: "label.remap.preset",
    ctrlId: "ctrl-remapPreset",
    options: [
      ["clean", "option.remap.preset.clean"],
      ["balanced", "option.remap.preset.balanced"],
      ["preserve", "option.remap.preset.preserve"],
      ["custom", "option.remap.preset.custom"],
    ] as const,
    value: store.get().remapPreset,
  });
  // Note: remapPresetRow.row is intentionally NOT appended to the DOM (B1).

  // ── Use raw + Advanced toggles ────────────────────────────────────────────
  const maskToggleRow = makeRow();
  maskToggleRow.classList.add("row-checkboxes");

  const useRawInput = document.createElement("input");
  useRawInput.type = "checkbox";
  useRawInput.id = "ctrl-useRawLabelmap";
  useRawInput.checked = store.get().useRawLabelmap;

  const useRawCluster = document.createElement("span");
  useRawCluster.className = "checkbox-cluster";

  const useRawLabel = document.createElement("label");
  useRawLabel.htmlFor = useRawInput.id;
  useRawLabel.dataset["i18n"] = "label.remap.useRaw";
  useRawLabel.textContent = t("label.remap.useRaw");

  // B3: info-btn tooltip for Use Raw checkbox.
  const useRawTip = createInfoTooltip(t("tip.useRaw"));
  onLocaleChange(() => useRawTip.setText(t("tip.useRaw")));

  useRawCluster.appendChild(useRawInput);
  useRawCluster.appendChild(useRawLabel);
  useRawCluster.appendChild(useRawTip.element);
  maskToggleRow.appendChild(useRawCluster);

  const advancedInput = document.createElement("input");
  advancedInput.type = "checkbox";
  advancedInput.id = "ctrl-remapAdvanced";
  advancedInput.setAttribute("aria-controls", "remap-advanced-panel");
  advancedInput.setAttribute("aria-expanded", "false");

  const advancedCluster = document.createElement("span");
  advancedCluster.className = "checkbox-cluster";

  const advancedLabel = document.createElement("label");
  advancedLabel.htmlFor = advancedInput.id;
  advancedLabel.dataset["i18n"] = "label.remap.advanced";
  advancedLabel.textContent = t("label.remap.advanced");
  // B3: info-btn tooltip for Advanced toggle.
  const advancedTip = createInfoTooltip(t("tip.advanced"));
  onLocaleChange(() => advancedTip.setText(t("tip.advanced")));

  advancedCluster.appendChild(advancedInput);
  advancedCluster.appendChild(advancedLabel);
  advancedCluster.appendChild(advancedTip.element);
  maskToggleRow.appendChild(advancedCluster);
  remapSection.body.appendChild(maskToggleRow);

  const advancedPanel = document.createElement("div");
  advancedPanel.id = "remap-advanced-panel";
  advancedPanel.className = "remap-advanced-panel";
  advancedPanel.hidden = true;
  remapSection.body.appendChild(advancedPanel);

  const remapDeltaERow = buildSliderRow({
    labelKey: "param.remap.deltaE",
    tipKey: "tip.remap.deltaE",
    ctrlId: "ctrl-remapDeltaE",
    min: 0,
    max: 20,
    step: 1,
    value: store.get().remapDeltaE,
    variant: "yellow",
  });
  advancedPanel.appendChild(remapDeltaERow.row);

  const remapButtonRow = makeRow();
  remapButtonRow.classList.add("remap-button-row");
  const remapButton = document.createElement("button");
  remapButton.type = "button";
  remapButton.className = "btn remap-run-btn";
  remapButton.dataset["i18n"] = "action.remap.run";
  remapButton.textContent = t("action.remap.run");
  // B3: info-btn tooltip for the Remap button.
  const remapBtnTip = createInfoTooltip(t("tip.remap"));
  onLocaleChange(() => remapBtnTip.setText(t("tip.remap")));
  remapButtonRow.appendChild(remapButton);
  remapButtonRow.appendChild(remapBtnTip.element);
  remapSection.body.appendChild(remapButtonRow);

  remapButton.addEventListener("click", () => {
    const state = store.get();
    const oldEntries = state.regionEntries;
    const patch = remapLabelmap(
      state,
      { maxK: state.remapMaxK, deltaE: state.remapDeltaE },
      (label) => t("label.regionN", { n: label }),
    );
    if (Object.keys(patch).length === 0) return;

    const undoStack = pushUndoSnapshot(state);
    store.patch({ ...patch, regionUndoStack: undoStack });

    const preserved = countPreservedEntries(
      oldEntries,
      store.get().regionEntries,
    );
    store.patch({ remapPreset: store.get().remapPreset });
    renderLabelmapStatus(null);
    labelmapStatusText = t("status.labelmap.remap.reset", { preserved });
    hasLabelmapStatus = true;
    syncRemapRegionInfo();
    syncStatusDisclosure();
  });

  // ── Cleanup operations (RR-3: flat under REMAP, no subhead) ──────────────
  // Drop islands and Re-segment operate on the mask data; previously under a
  // CLEANUP subhead which is now removed. Appended directly after Remap button.
  const dropIslandsBtn = document.createElement("button");
  dropIslandsBtn.type = "button";
  dropIslandsBtn.className = "region-list-toolbar-btn";
  dropIslandsBtn.dataset["i18n"] = "action.region.dropIslands";
  dropIslandsBtn.textContent = t("action.region.dropIslands");
  dropIslandsBtn.setAttribute("aria-label", t("aria.regionToolbar.dropIslands"));

  dropIslandsBtn.addEventListener("click", () => {
    const state = store.get();
    if (
      state.regionEntries.length === 0 ||
      state.regionLabelPlane.length === 0
    ) {
      return;
    }
    const imageSource = state.source ?? state.labelMap;
    if (!imageSource) return;
    const undoStack = pushUndoSnapshot(state);
    const patch = fillSmallIslands(
      {
        regionEntries: state.regionEntries,
        regionLabelPlane: state.regionLabelPlane,
        activeRegionUuids: state.activeRegionUuids,
        width: imageSource.width,
        height: imageSource.height,
        labelMap: state.labelMap,
        regions: state.regions,
      },
      state.fillIslandsMinPixels,
    );
    store.patch({ ...patch, regionUndoStack: undoStack });
  });

  const refloodBtn = document.createElement("button");
  refloodBtn.type = "button";
  refloodBtn.className = "region-list-toolbar-btn";
  refloodBtn.dataset["i18n"] = "action.region.resegment";
  refloodBtn.textContent = t("action.region.resegment");
  refloodBtn.setAttribute("aria-label", t("aria.regionToolbar.resegment"));
  refloodBtn.addEventListener("click", () => {
    store.patch({ refloodMode: !store.get().refloodMode });
  });

  const maskOpsRow = makeRow();
  maskOpsRow.classList.add("area-mask-ops-row");
  maskOpsRow.appendChild(dropIslandsBtn);
  maskOpsRow.appendChild(refloodBtn);
  remapSection.body.appendChild(maskOpsRow);

  const minPixelsRow = makeRow();
  minPixelsRow.classList.add("region-list-min-pixels-row");

  const minPixelsRange = makeRange(
    1,
    1000,
    1,
    clampMinIslandPixels(store.get().fillIslandsMinPixels),
  );
  minPixelsRange.id = "region-min-island-pixels";

  const minPixelsLabel = makeLabelText(
    "param.regionOps.minPixels",
    "tip.minIslandPixels",
    minPixelsRange.id,
  );

  const minPixelsCluster = document.createElement("span");
  minPixelsCluster.className =
    "row-value-cluster region-list-min-pixels-value-cluster";

  const minPixelsSlider = wrapRangeAsSlider(minPixelsRange, "violet");

  const minPixelsValueInput = document.createElement("input");
  minPixelsValueInput.type = "number";
  minPixelsValueInput.className = "row-value region-list-min-pixels-value";
  minPixelsValueInput.min = "1";
  minPixelsValueInput.max = "1000";
  minPixelsValueInput.step = "1";
  minPixelsValueInput.value = minPixelsRange.value;

  const minPixelsSuffix = document.createElement("span");
  minPixelsSuffix.className = "value-suffix";
  minPixelsSuffix.textContent = "px²";

  const minPixelsHint = document.createElement("p");
  minPixelsHint.className = "row-hint region-list-min-pixels-hint";
  minPixelsHint.dataset["i18n"] = "hint.minIslandPixels.recommended";
  minPixelsHint.textContent = t("hint.minIslandPixels.recommended");
  onLocaleChange(() => {
    minPixelsHint.textContent = t("hint.minIslandPixels.recommended");
  });

  const minPixelsStepper = makeStepperButtons(minPixelsRange);
  minPixelsRow.appendChild(minPixelsLabel);
  minPixelsCluster.appendChild(minPixelsSlider);
  minPixelsCluster.appendChild(minPixelsValueInput);
  minPixelsCluster.appendChild(minPixelsSuffix);
  minPixelsCluster.appendChild(minPixelsStepper);
  minPixelsRow.appendChild(minPixelsCluster);
  minPixelsRow.appendChild(minPixelsHint);
  remapSection.body.appendChild(minPixelsRow);

  let syncingMinPixels = false;

  function reflectMinPixels(value: number, repaintSlider: boolean): void {
    const text = String(clampMinIslandPixels(value));
    minPixelsRange.value = text;
    minPixelsValueInput.value = text;
    if (!repaintSlider) return;
    syncingMinPixels = true;
    try {
      minPixelsRange.dispatchEvent(new Event("input", { bubbles: true }));
    } finally {
      syncingMinPixels = false;
    }
  }

  minPixelsRange.addEventListener("input", () => {
    const value = clampMinIslandPixels(Number(minPixelsRange.value));
    reflectMinPixels(value, false);
    if (!syncingMinPixels) {
      store.patch({ fillIslandsMinPixels: value });
    }
  });

  function commitMinPixelsNumber(): void {
    const value = clampMinIslandPixels(Number(minPixelsValueInput.value));
    reflectMinPixels(value, true);
    store.patch({ fillIslandsMinPixels: value });
  }

  minPixelsValueInput.addEventListener("input", commitMinPixelsNumber);
  minPixelsValueInput.addEventListener("change", commitMinPixelsNumber);

  useRawInput.addEventListener("change", () => {
    store.patch({ useRawLabelmap: useRawInput.checked });
  });

  advancedInput.addEventListener("change", () => {
    advancedPanel.hidden = !advancedInput.checked;
    advancedInput.setAttribute("aria-expanded", String(advancedInput.checked));
  });

  // ── Wiring: preset → sliders, sliders → 'custom' preset ──────────────────

  function applyPreset(preset: Exclude<RemapPreset, "custom">): void {
    const config = PRESET_CONFIGS[preset];
    remapMaxKRow.input.value = String(config.maxK);
    remapMaxKRow.valueEl.textContent = String(config.maxK);
    remapDeltaERow.input.value = String(config.deltaE);
    remapDeltaERow.valueEl.textContent = String(config.deltaE);
    store.patch({
      remapMaxK: config.maxK,
      remapDeltaE: config.deltaE,
      remapPreset: preset,
    });
  }

  function detectPresetFromSliders(maxK: number, deltaE: number): RemapPreset {
    for (const [key, config] of Object.entries(PRESET_CONFIGS) as [
      Exclude<RemapPreset, "custom">,
      { maxK: number; deltaE: number },
    ][]) {
      if (config.maxK === maxK && config.deltaE === deltaE) return key;
    }
    return "custom";
  }

  remapPresetRow.select.addEventListener("change", () => {
    const value = remapPresetRow.select.value as RemapPreset;
    if (value !== "custom") {
      applyPreset(value);
    }
  });

  remapMaxKRow.input.addEventListener("input", () => {
    const v = Number(remapMaxKRow.input.value);
    remapMaxKRow.valueEl.textContent = String(v);
    const newPreset = detectPresetFromSliders(v, store.get().remapDeltaE);
    store.patch({ remapMaxK: v, remapPreset: newPreset });
    remapPresetRow.select.value = newPreset;
    remapPresetRow.refresh();
  });

  remapDeltaERow.input.addEventListener("input", () => {
    const v = Number(remapDeltaERow.input.value);
    remapDeltaERow.valueEl.textContent = String(v);
    const newPreset = detectPresetFromSliders(store.get().remapMaxK, v);
    store.patch({ remapDeltaE: v, remapPreset: newPreset });
    remapPresetRow.select.value = newPreset;
    remapPresetRow.refresh();
  });

  store.subscribe((state, patch) => {
    if (useRawInput.checked !== state.useRawLabelmap) {
      useRawInput.checked = state.useRawLabelmap;
    }
    if ("regionEntries" in patch || "labelMap" in patch || "regions" in patch) {
      syncRemapRegionInfo();
    }
    if ("labelMap" in patch || "regionEntries" in patch || "regionLabelPlane" in patch) {
      syncMaskOpsEnabled();
    }
    if ("refloodMode" in patch) {
      syncRefloodButton();
    }
    if ("fillIslandsMinPixels" in patch) {
      const next = clampMinIslandPixels(state.fillIslandsMinPixels);
      const nextText = String(next);
      if (
        minPixelsRange.value !== nextText ||
        minPixelsValueInput.value !== nextText
      ) {
        reflectMinPixels(next, true);
      }
    }
    if ("labelMap" in patch) {
      syncMaskButtonText();
      syncMaskLoadedState();
      // R10 gating: show REMAP section only when a labelmap mask is loaded.
      remapSection.section.hidden = state.labelMap === null;
    }
  });

  function syncMaskOpsEnabled(): void {
    const state = store.get();
    const canUseMaskOps =
      state.regionEntries.length >= 1 && state.regionLabelPlane.length > 0;
    for (const button of [dropIslandsBtn, refloodBtn]) {
      button.disabled = !canUseMaskOps;
      if (canUseMaskOps) {
        button.removeAttribute("aria-disabled");
      } else {
        button.setAttribute("aria-disabled", "true");
      }
    }
  }

  function syncRefloodButton(): void {
    const active = store.get().refloodMode;
    refloodBtn.setAttribute("aria-pressed", String(active));
    refloodBtn.classList.toggle("active", active);
  }
  syncMaskOpsEnabled();
  syncRefloodButton();

  // Sync i18n text for all [data-i18n] elements and select option labels
  // within both area-mask sections. This mirrors what createControls.syncI18n
  // does for the .controls aside - needed because createAreaMaskSection
  // renders into the Region panel, not the Tone panel aside.
  function syncAreaMaskI18n(): void {
    for (const sectionEl of [labelmapSection.section, remapSection.section]) {
      sectionEl.querySelectorAll<HTMLElement>("[data-i18n]").forEach((el) => {
        const key = el.dataset["i18n"];
        if (!key) return;
        if (el.children.length > 0) return;
        el.textContent = t(key);
      });
    }
    // Sync select option labels and refresh the cselect button.
    Array.from(remapPresetRow.select.options).forEach((opt) => {
      const key = opt.dataset["i18n"];
      if (key) opt.textContent = t(key);
    });
    remapPresetRow.refresh();
    // The .area-status-live region carries a fully-localized phrase that
    // is built imperatively (not via [data-i18n]) so refresh it here too.
    syncRemapRegionInfo();
  }
  syncAreaMaskI18n();
  onLocaleChange(syncAreaMaskI18n);

  return {
    setLabelmapStatus: renderLabelmapStatus,
  };
}

export function createControls(
  parent: HTMLElement,
  store: Store,
): ControlsHandles {
  const root = document.createElement("aside");
  root.className = "controls";
  parent.appendChild(root);
  root.addEventListener("click", (event) => {
    const target = event.target;
    if (target instanceof Element && target.closest(".info-btn")) return;
    closeOpenTooltips();
  });

  /**
   * Route a method-control change through applyMethodPartial.
   * When regions are active, the partial is applied to every selected
   * region's override AND mirrored to global state so sliders show the
   * updated value immediately. When no region is active (single-image /
   * virtual Default mode), global state is patched directly - existing
   * behaviour unchanged.
   */
  function patchMethod(partial: MethodPartial): void {
    const next = applyMethodPartial(store.get(), partial);
    // 3.1: auto-revert tonePreset to 'custom' when a preset-controlled field
    // is manually edited. PRESET_CONTROLLED_KEYS covers every field that any
    // named preset controls.
    const touchesPreset = (Object.keys(partial) as (keyof DemoState)[]).some(
      (k) => PRESET_CONTROLLED_KEYS.has(k),
    );
    if (touchesPreset && store.get().tonePreset !== "custom") {
      next.tonePreset = "custom";
    }
    store.patch(next);
  }

  function hasMethodSelectionTarget(state: DemoState): boolean {
    if (state.activeStackUuid !== null) {
      return state.regionStacks.some((stack) => stack.uuid === state.activeStackUuid);
    }
    if (state.activeRegionUuids.length === 0) return false;
    const entryUuids = new Set(state.regionEntries.map((entry) => entry.uuid));
    return state.activeRegionUuids.some((uuid) => entryUuids.has(uuid));
  }

  // B1: the AREA MASK section (01 LABELMAP) is now rendered into the Region
  // tab panel by main.ts via createAreaMaskSection(). It no longer lives here.

  // ========== 01 TONE PRESET - violet badge per I1 ==========
  // Extracted to controls/preset-crud.ts (CSS-04.5). Returns refs consumed
  // by syncI18n and the tonePreset state subscriber below.
  const { presetSelect, syncExportEnabled } = setupPresetCrud({
    store,
    root,
    makeSection,
    makeRow,
    makeLabelText,
  });

  // Extracted to controls/method.ts (CSS-04.2). Returns the family + algo
  // SelectRow refs consumed by syncVisibility, syncSliderValues, and syncI18n.
  const { family, algo } = setupMethodSection({
    store,
    root,
    makeSection,
    buildSelectRow,
    patchMethod,
    createFamilyStarterPatch,
  });

  // ========== 03 SOURCE - violet badge per I1 ==========
  // Extracted to controls/source-adjust.ts (CSS-04). Passes the private
  // helper functions as dependencies so the module can build the section
  // without duplicating their implementations.
  setupSourceAdjust({ store, root, makeSection, makeRow, buildSliderRow });

  // ========== 04 BLACK / WHITE THRESHOLDS - removed per I3 ==========
  // The UI controls are intentionally removed ("不便なので消して良い"). The
  // blackThreshold and whiteThreshold state fields are preserved because the
  // render pipeline reads them; they simply use their defaults (0.02 / 0.98).

  // ========== 04a/04b/04c HALFTONE / CUSTOM PATTERN / DITHERTONE + 05 NOISE ==========
  // Extracted to controls/family-panels.ts (CSS-04.4). Returns all panel refs
  // consumed by syncVisibility, syncSliderValues, and syncI18n below.
  const {
    halftoneSection,
    customPatternSection,
    dithertoneSection,
    noiseSection,
    aPanel,
    cellSize,
    density,
    polarity,
    matrixRow,
    matrixSelect,
    serpentineRow,
    serpentineInput,
    fsStrength,
    brightness,
    contrast,
    gridAngle,
    algorithmJitter,
    aPatternNoiseScale,
    grainStrength,
    grainScale,
    stampGroup,
    stampType,
    coverage,
    shapeAngle,
    shapeFlipX,
    shapeFlipY,
    stampUploadRow,
    patternBlendMode,
    patternInvertRow,
    patternInvertInput,
    patternBrightness,
    patternContrast,
    patternOpacity,
    patternScale,
    patternRotation,
    patternFlipX,
    patternFlipY,
    patternNoise,
    patternNoiseScale,
    blur,
    patternHalftoneEnabledInput,
    patternHalftoneGroup,
    patternHalftoneCellSize,
    patternHalftoneStampType,
    patternHalftoneStampUpload,
    patternHalftoneCoverage,
    patternHalftonePolarity,
    patternHalftoneDensity,
    patternHalftoneBlackThreshold,
    patternHalftoneWhiteThreshold,
    patternHalftoneGridAngle,
    patternHalftoneShapeAngle,
    patternHalftoneShapeFlipX,
    patternHalftoneShapeFlipY,
    patternHalftoneAlgorithmJitter,
  } = setupFamilyPanels({
    store,
    root,
    patchMethod,
    getIsSyncing: () => isSyncing,
    hasMethodSelectionTarget,
    family,
    makeSection,
    makeRow,
    makeLabelText,
    buildSliderRow,
    buildSelectRow,
    buildFileRow,
    makeCustomSelect,
    readImageDataFromFile,
  });

  // ========== 05 OUTPUT ==========
  const { exportScale, exportButton, copyButton } = setupOutputSection({
    root,
    makeSection,
    buildSelectRow,
  });

  // ========== i18n + visibility sync ==========
  function syncI18n(): void {
    [root].forEach((scope) => {
      scope.querySelectorAll<HTMLElement>("[data-i18n]").forEach((el) => {
        const key = el.dataset["i18n"];
        if (!key) return;
        if (el.children.length > 0) return;
        el.textContent = t(key);
      });
    });
    root.querySelectorAll<HTMLButtonElement>("[data-tooltip]").forEach((el) => {
      const key = el.dataset["tooltip"];
      if (!key) return;
      const text = t(key);
      el.dataset["tooltipText"] = text;
      el.setAttribute("aria-label", text);
    });
    [
      presetSelect,
      family.select,
      algo.select,
      polarity.select,
      stampType.select,
      patternBlendMode.select,
      patternHalftoneStampType.select,
      patternHalftonePolarity.select,
      exportScale.select,
    ].forEach((sel) => {
      Array.from(sel.options).forEach((opt) => {
        const key = opt.dataset["i18n"];
        if (key) opt.textContent = t(key);
      });
      // After option text changes, refresh the cselect button label so it
      // doesn't read stale (or empty) `option.textContent`.
      sel.dispatchEvent(new CustomEvent("cselect:sync"));
    });
  }
  syncI18n();
  onLocaleChange(syncI18n);

  // Visibility per family (v0.2.1):
  // - Family A "Halftone": single algorithm (blue-noise stochastic
  //   per-cell jitter), no algorithm picker. The stamp panel shows
  //   for every stamp shape.
  // - Family B "Custom pattern": its own panel only. Stamp panel
  //   hidden.
  // - Family C "Stamp threshold" (v0.2.0 Family A behaviour): full
  //   algorithm picker. Every algorithm still goes through Family C's
  //   cell mask to stamp render path, so dot size, stamp shape, shape
  //   transform and grid angle remain visible for all Family C variants.
  function syncVisibility(state: DemoState): void {
    // Derive effective family + algorithm and ALL dial values from the active
    // selection via effectiveDialValues (shared source of truth with syncSliderValues).
    // Priority: active stack override → active region override → global state.
    // Stale activeStackUuid (stack not found) correctly falls through to the
    // per-region or global level - no divergence with the status bar.
    const eff = effectiveDialValues(state);
    const { family: effectiveFamily, algorithmA: effectiveAlgorithmA } = eff;

    const isFamilyA = effectiveFamily === "A";
    const isFamilyB = effectiveFamily === "B";
    const isFamilyC = effectiveFamily === "C";
    const stampPanelVisible = isFamilyA || isFamilyC;

    // M2: show only the section for the active family; hide the others.
    halftoneSection.section.hidden = !isFamilyA;
    customPatternSection.section.hidden = !isFamilyB;
    dithertoneSection.section.hidden = !isFamilyC;

    // M2 DOM-move: aPanel is shared between Family A (Halftone) and Family C
    // (Dithertone) since both use the same dial set with row-level toggles.
    // Move it into whichever section is currently visible so the section body
    // is never empty.
    if (isFamilyC && aPanel.parentElement !== dithertoneSection.body) {
      dithertoneSection.body.appendChild(aPanel);
    } else if (!isFamilyC && aPanel.parentElement !== halftoneSection.body) {
      halftoneSection.body.appendChild(aPanel);
    }

    // patternInvert is a primary Custom Pattern control. Keep it visible for
    // every Family B blend mode, including multiply.
    patternInvertRow.style.display = isFamilyB ? "" : "none";
    patternHalftoneGroup.style.display =
      isFamilyB && eff.patternHalftoneEnabled ? "" : "none";
    patternHalftoneStampUpload.row.style.display =
      isFamilyB &&
      eff.patternHalftoneEnabled &&
      eff.patternHalftoneStampType === "custom"
        ? ""
        : "none";

    // Algorithm picker only shown in METHOD section for Family C.
    algo.row.style.display = isFamilyC ? "" : "none";

    // M2/F1: NOISE section visible for all families. Family C owns the
    // grain rows; Family A/B retain their existing strength/size rows.
    // Badge variant alternates with the active family section: Family A
    // (yellow halftone) → NOISE violet; Family B (violet pattern) → NOISE yellow.
    noiseSection.section.hidden = false;
    const noiseSectionBadge =
      noiseSection.head.querySelector<HTMLSpanElement>(".ix");
    if (noiseSectionBadge) {
      noiseSectionBadge.className = isFamilyA ? "ix ix-violet" : "ix ix-yellow";
      noiseSection.head.className = isFamilyA
        ? "section-head violet"
        : "section-head yellow";
    }

    // M2: show correct noise rows per family; all rows always remain in the DOM.
    // Family A: algorithmJitter (strength) + aPatternNoiseScale (size).
    // Family B: patternNoise (strength) + patternNoiseScale (size).
    algorithmJitter.row.style.display = isFamilyA ? "" : "none";
    aPatternNoiseScale.row.style.display = isFamilyA ? "" : "none";
    patternNoise.row.style.display = isFamilyB ? "" : "none";
    patternNoiseScale.row.style.display = isFamilyB ? "" : "none";
    grainStrength.row.style.display = isFamilyC ? "" : "none";
    grainScale.row.style.display = isFamilyC ? "" : "none";

    stampGroup.style.display = stampPanelVisible ? "" : "none";

    cellSize.row.style.display = "";
    matrixRow.style.display =
      isFamilyC && effectiveAlgorithmA === "bayer" ? "" : "none";
    serpentineRow.style.display =
      isFamilyC && effectiveAlgorithmA === "floyd-steinberg" ? "" : "none";
    fsStrength.row.style.display =
      isFamilyC &&
      (effectiveAlgorithmA === "floyd-steinberg" ||
        effectiveAlgorithmA === "atkinson")
        ? ""
        : "none";
    brightness.row.style.display =
      isFamilyC && effectiveAlgorithmA === "werness" ? "" : "none";
    contrast.row.style.display =
      isFamilyC && effectiveAlgorithmA === "werness" ? "" : "none";

    gridAngle.row.style.display = "";

    coverage.row.style.display = stampPanelVisible ? "" : "none";
    // Read stampType from effective values so it reflects the active region override.
    stampUploadRow.style.display =
      stampPanelVisible && eff.stampType === "custom" ? "" : "none";

    const shapeAngleApplies =
      eff.stampType === "diamond" ||
      eff.stampType === "line" ||
      eff.stampType === "cross" ||
      eff.stampType === "ellipse" ||
      eff.stampType === "asterisk" ||
      eff.stampType === "flower" ||
      eff.stampType === "star" ||
      eff.stampType === "heart" ||
      eff.stampType === "spade" ||
      eff.stampType === "club" ||
      eff.stampType === "custom";
    shapeAngle.row.style.display =
      stampPanelVisible && shapeAngleApplies ? "" : "none";
    shapeFlipX.row.style.display = stampPanelVisible ? "" : "none";
    shapeFlipY.row.style.display = stampPanelVisible ? "" : "none";
    const patternHalftoneShapeAngleApplies =
      eff.patternHalftoneStampType === "diamond" ||
      eff.patternHalftoneStampType === "line" ||
      eff.patternHalftoneStampType === "cross" ||
      eff.patternHalftoneStampType === "ellipse" ||
      eff.patternHalftoneStampType === "asterisk" ||
      eff.patternHalftoneStampType === "flower" ||
      eff.patternHalftoneStampType === "star" ||
      eff.patternHalftoneStampType === "heart" ||
      eff.patternHalftoneStampType === "spade" ||
      eff.patternHalftoneStampType === "club" ||
      eff.patternHalftoneStampType === "custom";
    patternHalftoneShapeAngle.row.style.display =
      isFamilyB && eff.patternHalftoneEnabled && patternHalftoneShapeAngleApplies
        ? ""
        : "none";
    patternHalftoneShapeFlipX.row.style.display =
      isFamilyB && eff.patternHalftoneEnabled ? "" : "none";
    patternHalftoneShapeFlipY.row.style.display =
      isFamilyB && eff.patternHalftoneEnabled ? "" : "none";

    // R9 L-2: assign data-pos-parity to every visible section so CSS can
    // alternate slider fill / thumb colour by DOM position rather than by
    // hardcoded per-slider variant. Runs after all hidden flags are resolved
    // above so the visible-position count is correct.
    // The hidden outputSection is excluded automatically (hidden=true).
    const visibleSections = Array.from(
      root.querySelectorAll<HTMLElement>('.section'),
    ).filter(s => !s.hidden);
    visibleSections.forEach((s, i) => {
      s.dataset['posParity'] = (i + 1) % 2 === 0 ? 'even' : 'odd';
    });
  }

  /**
   * Guard flag to prevent oninput handlers from calling patchMethod during a
   * programmatic sync. Set to true while syncSliderValues is running; the
   * oninput handlers check this flag and return early if it is set.
   */
  let isSyncing = false;

  /**
   * Write a new value to a range input and dispatch a synthetic `input` event
   * so that the custom-slider fill bar repaints (wrapRangeAsSlider listens to
   * `input`). Skips both the write and the dispatch when the value is already
   * current (avoids unnecessary DOM churn).
   *
   * Must only be called while isSyncing is true so that the oninput handlers
   * triggered by the synthetic event are no-ops during the sync pass.
   */
  function syncRangeValue(input: HTMLInputElement, value: number): void {
    if (input.value === String(value)) return;
    input.value = String(value);
    input.dispatchEvent(new Event("input", { bubbles: true }));
  }

  /**
   * Sync every slider / select to the effective dial values for the current
   * active selection. Uses effectiveDialValues(state) to read from the active
   * stack / region override when one is in effect, falling back to global state.
   *
   * Only updates DOM elements when the value has actually changed to avoid
   * spurious repaints and re-dispatch loops.
   *
   * The `oninput`/`onchange` handlers on each control are left UNCHANGED -
   * they still dispatch patchMethod() with the user's typed value. This
   * function only controls what is DISPLAYED when state changes externally.
   */
  function syncSliderValues(state: DemoState): void {
    const v = effectiveDialValues(state);

    // Guard against re-entrant patchMethod calls triggered by the synthetic
    // input events that syncRangeValue dispatches for fill-bar repaints.
    isSyncing = true;
    try {
      // Sync family + algorithm selects so the correct option is selected when
      // the user switches active region / stack.
      if (family.select.value !== v.family) {
        family.select.value = v.family;
        family.select.dispatchEvent(new CustomEvent("cselect:sync"));
      }
      if (algo.select.value !== v.algorithmA) {
        algo.select.value = v.algorithmA;
        algo.select.dispatchEvent(new CustomEvent("cselect:sync"));
      }

      // ---- Family A / C common sliders ----
      if (Number(cellSize.input.value) !== v.cellSize) {
        syncRangeValue(cellSize.input, v.cellSize);
        cellSize.valueEl.textContent = String(v.cellSize);
      }
      if (Number(density.input.value) !== v.density) {
        syncRangeValue(density.input, v.density);
        density.valueEl.textContent = String(v.density);
      }
      if (polarity.select.value !== v.polarity) {
        polarity.select.value = v.polarity;
        polarity.select.dispatchEvent(new CustomEvent("cselect:sync"));
      }
      if (matrixSelect.value !== String(v.matrixSize)) {
        matrixSelect.value = String(v.matrixSize);
        matrixSelect.dispatchEvent(new CustomEvent("cselect:sync"));
      }
      if (serpentineInput.checked !== v.serpentine) {
        serpentineInput.checked = v.serpentine;
      }
      // The strength slider is shared between floyd-steinberg and atkinson.
      // Route to the correct field based on the effective algorithm so that a
      // Family C atkinson override displays its own atkinsonStrength, not the
      // global fsStrength.
      {
        const strengthValue =
          v.algorithmA === "atkinson" ? v.atkinsonStrength : v.fsStrength;
        if (Number(fsStrength.input.value) !== strengthValue) {
          syncRangeValue(fsStrength.input, strengthValue);
          fsStrength.valueEl.textContent = strengthValue.toFixed(2);
        }
      }
      if (Number(brightness.input.value) !== v.brightness) {
        syncRangeValue(brightness.input, v.brightness);
        brightness.valueEl.textContent = String(v.brightness);
      }
      if (Number(contrast.input.value) !== v.contrast) {
        syncRangeValue(contrast.input, v.contrast);
        contrast.valueEl.textContent = String(v.contrast);
      }
      if (Number(gridAngle.input.value) !== v.gridAngleDeg) {
        syncRangeValue(gridAngle.input, v.gridAngleDeg);
        gridAngle.valueEl.textContent = `${v.gridAngleDeg}°`;
      }
      if (Number(algorithmJitter.input.value) !== v.algorithmJitter) {
        syncRangeValue(algorithmJitter.input, v.algorithmJitter);
        algorithmJitter.valueEl.textContent = v.algorithmJitter.toFixed(2);
      }
      if (Number(grainStrength.input.value) !== v.grainStrength) {
        syncRangeValue(grainStrength.input, v.grainStrength);
        grainStrength.valueEl.textContent = v.grainStrength.toFixed(2);
      }
      if (Number(grainScale.input.value) !== v.grainScale) {
        syncRangeValue(grainScale.input, v.grainScale);
        grainScale.valueEl.textContent = v.grainScale.toFixed(1);
      }

      // ---- Stamp sub-panel ----
      if (stampType.select.value !== v.stampType) {
        stampType.select.value = v.stampType;
        stampType.select.dispatchEvent(new CustomEvent("cselect:sync"));
      }
      if (Number(coverage.input.value) !== v.stampCoverage) {
        syncRangeValue(coverage.input, v.stampCoverage);
        coverage.valueEl.textContent = v.stampCoverage.toFixed(2);
      }
      if (Number(shapeAngle.input.value) !== v.shapeAngleDeg) {
        syncRangeValue(shapeAngle.input, v.shapeAngleDeg);
        shapeAngle.valueEl.textContent = `${v.shapeAngleDeg}°`;
      }
      if (shapeFlipX.input.checked !== v.shapeFlipX) {
        shapeFlipX.input.checked = v.shapeFlipX;
      }
      if (shapeFlipY.input.checked !== v.shapeFlipY) {
        shapeFlipY.input.checked = v.shapeFlipY;
      }

      // ---- Family B sliders ----
      if (patternBlendMode.select.value !== v.patternBlendMode) {
        patternBlendMode.select.value = v.patternBlendMode;
        patternBlendMode.select.dispatchEvent(new CustomEvent("cselect:sync"));
      }
      if (patternInvertInput.checked !== v.patternInvert) {
        patternInvertInput.checked = v.patternInvert;
      }
      if (Number(patternBrightness.input.value) !== v.patternBrightness) {
        syncRangeValue(patternBrightness.input, v.patternBrightness);
        patternBrightness.valueEl.textContent = String(v.patternBrightness);
      }
      if (Number(patternContrast.input.value) !== v.patternContrast) {
        syncRangeValue(patternContrast.input, v.patternContrast);
        patternContrast.valueEl.textContent = String(v.patternContrast);
      }
      if (Number(patternOpacity.input.value) !== v.patternOpacity) {
        syncRangeValue(patternOpacity.input, v.patternOpacity);
        patternOpacity.valueEl.textContent = v.patternOpacity.toFixed(2);
      }
      if (Number(patternScale.input.value) !== v.patternScale) {
        syncRangeValue(patternScale.input, v.patternScale);
        patternScale.valueEl.textContent = v.patternScale.toFixed(2);
      }
      if (Number(patternRotation.input.value) !== v.patternRotationDeg) {
        syncRangeValue(patternRotation.input, v.patternRotationDeg);
        patternRotation.valueEl.textContent = `${v.patternRotationDeg}°`;
      }
      if (patternFlipX.input.checked !== v.patternFlipX) {
        patternFlipX.input.checked = v.patternFlipX;
      }
      if (patternFlipY.input.checked !== v.patternFlipY) {
        patternFlipY.input.checked = v.patternFlipY;
      }
      if (patternHalftoneEnabledInput.checked !== v.patternHalftoneEnabled) {
        patternHalftoneEnabledInput.checked = v.patternHalftoneEnabled;
      }
      if (Number(patternHalftoneCellSize.input.value) !== v.patternHalftoneCellSize) {
        syncRangeValue(patternHalftoneCellSize.input, v.patternHalftoneCellSize);
        patternHalftoneCellSize.valueEl.textContent = String(v.patternHalftoneCellSize);
      }
      if (patternHalftoneStampType.select.value !== v.patternHalftoneStampType) {
        patternHalftoneStampType.select.value = v.patternHalftoneStampType;
        patternHalftoneStampType.select.dispatchEvent(new CustomEvent("cselect:sync"));
      }
      if (
        Number(patternHalftoneCoverage.input.value) !==
        v.patternHalftoneStampCoverage
      ) {
        syncRangeValue(patternHalftoneCoverage.input, v.patternHalftoneStampCoverage);
        patternHalftoneCoverage.valueEl.textContent =
          v.patternHalftoneStampCoverage.toFixed(2);
      }
      if (patternHalftonePolarity.select.value !== v.patternHalftonePolarity) {
        patternHalftonePolarity.select.value = v.patternHalftonePolarity;
        patternHalftonePolarity.select.dispatchEvent(new CustomEvent("cselect:sync"));
      }
      if (Number(patternHalftoneDensity.input.value) !== v.patternHalftoneDensity) {
        syncRangeValue(patternHalftoneDensity.input, v.patternHalftoneDensity);
        patternHalftoneDensity.valueEl.textContent = String(v.patternHalftoneDensity);
      }
      if (
        Number(patternHalftoneBlackThreshold.input.value) !==
        v.patternHalftoneBlackThreshold
      ) {
        syncRangeValue(
          patternHalftoneBlackThreshold.input,
          v.patternHalftoneBlackThreshold,
        );
        patternHalftoneBlackThreshold.valueEl.textContent =
          v.patternHalftoneBlackThreshold.toFixed(2);
      }
      if (
        Number(patternHalftoneWhiteThreshold.input.value) !==
        v.patternHalftoneWhiteThreshold
      ) {
        syncRangeValue(
          patternHalftoneWhiteThreshold.input,
          v.patternHalftoneWhiteThreshold,
        );
        patternHalftoneWhiteThreshold.valueEl.textContent =
          v.patternHalftoneWhiteThreshold.toFixed(2);
      }
      if (
        Number(patternHalftoneGridAngle.input.value) !==
        v.patternHalftoneGridAngleDeg
      ) {
        syncRangeValue(patternHalftoneGridAngle.input, v.patternHalftoneGridAngleDeg);
        patternHalftoneGridAngle.valueEl.textContent =
          `${v.patternHalftoneGridAngleDeg}°`;
      }
      if (
        Number(patternHalftoneShapeAngle.input.value) !==
        v.patternHalftoneShapeAngleDeg
      ) {
        syncRangeValue(
          patternHalftoneShapeAngle.input,
          v.patternHalftoneShapeAngleDeg,
        );
        patternHalftoneShapeAngle.valueEl.textContent =
          `${v.patternHalftoneShapeAngleDeg}°`;
      }
      if (
        patternHalftoneShapeFlipX.input.checked !==
        v.patternHalftoneShapeFlipX
      ) {
        patternHalftoneShapeFlipX.input.checked = v.patternHalftoneShapeFlipX;
      }
      if (
        patternHalftoneShapeFlipY.input.checked !==
        v.patternHalftoneShapeFlipY
      ) {
        patternHalftoneShapeFlipY.input.checked = v.patternHalftoneShapeFlipY;
      }
      if (
        Number(patternHalftoneAlgorithmJitter.input.value) !==
        v.patternHalftoneAlgorithmJitter
      ) {
        syncRangeValue(
          patternHalftoneAlgorithmJitter.input,
          v.patternHalftoneAlgorithmJitter,
        );
        patternHalftoneAlgorithmJitter.valueEl.textContent =
          v.patternHalftoneAlgorithmJitter.toFixed(2);
      }
      if (Number(blur.input.value) !== v.blurRadius) {
        syncRangeValue(blur.input, v.blurRadius);
        blur.valueEl.textContent = `${v.blurRadius}px`;
      }
      if (Number(patternNoise.input.value) !== v.patternNoise) {
        syncRangeValue(patternNoise.input, v.patternNoise);
        patternNoise.valueEl.textContent = v.patternNoise.toFixed(2);
      }
      if (Number(patternNoiseScale.input.value) !== v.patternNoiseScale) {
        syncRangeValue(patternNoiseScale.input, v.patternNoiseScale);
        patternNoiseScale.valueEl.textContent = v.patternNoiseScale.toFixed(1);
      }
      // 3.4: sync the Family A copy of patternNoiseScale too.
      if (Number(aPatternNoiseScale.input.value) !== v.patternNoiseScale) {
        syncRangeValue(aPatternNoiseScale.input, v.patternNoiseScale);
        aPatternNoiseScale.valueEl.textContent = v.patternNoiseScale.toFixed(1);
      }
    } finally {
      isSyncing = false;
    }
  }

  // 3.1: sync the tone preset dropdown when tonePreset changes in state.
  store.subscribe((state, patch) => {
    if ("tonePreset" in patch) {
      if (presetSelect.value !== state.tonePreset) {
        presetSelect.value = state.tonePreset;
        presetSelect.dispatchEvent(new CustomEvent("cselect:sync"));
      }
      syncExportEnabled();
    }
  });

  syncVisibility(store.get());
  syncSliderValues(store.get());
  store.subscribe((state) => {
    syncVisibility(state);
    syncSliderValues(state);
  });

  return {
    exportScaleSelect: exportScale.select,
    exportButton,
    copyButton,
  };
}
